import React, { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { MdFileDownload, MdRefresh, MdOpenInFull } from 'react-icons/md';
import { buildTooltip } from '../../Utils/AnalyticsConfig';
import { theme } from '../../Utils/theme';
import InfoHint from '../Common/InfoHint';
import ChartZoomModal from './ChartZoomModal';

// Module-level singleton: all BaseChart instances share one load attempt
// instead of racing to import/fetch/register the same map independently.
let usaMapPromise = null;

const ensureUSAMapRegistered = () => {
  if (echarts.getMap && echarts.getMap('USA')) {
    return Promise.resolve(true);
  }

  if (!usaMapPromise) {
    usaMapPromise = (async () => {
      let usaGeoJson;

      try {
        const mod = await import('../../Assets/usa.json');
        usaGeoJson = mod.default || mod;
      } catch (loadErr) {
        try {
          const response = await fetch('https://echarts.apache.org/examples/data/asset/geo/USA.json');
          if (!response.ok) throw new Error(`Failed to fetch USA map: ${response.statusText}`);
          usaGeoJson = await response.json();
        } catch (fetchErr) {
          console.error('Failed to load USA map geojson:', fetchErr);
          usaMapPromise = null; // allow a future retry
          return false;
        }
      }

      // Guard against malformed/missing GeoJSON before handing it to ECharts
      if (!usaGeoJson || !Array.isArray(usaGeoJson.features)) {
        console.error('USA map geojson is missing or malformed; skipping registration.');
        usaMapPromise = null;
        return false;
      }

      echarts.registerMap('USA', usaGeoJson);
      return true;
    })();
  }

  return usaMapPromise;
};

const BaseChart = React.memo(function BaseChart({
  title,
  subtitle,
  option,
  height = '400px',
  className = '',
  loading = false,
  noData = false,
  error = null,
  style = {},
  info = null,
  toolbarActions = null,
}) {
  const [zoomOpen, setZoomOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const chartRef = useRef(null);

  const handleOpenZoom = useCallback(() => setZoomOpen(true), []);
  const handleCloseZoom = useCallback(() => setZoomOpen(false), []);
  const handleExportPNG = useCallback(() => {
    const instance = chartRef.current?.getEchartsInstance?.();
    if (!instance) return;
    const url = instance.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: theme.chartZoomTrackBg() });
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title || 'chart'}.png`;
    link.click();
  }, [title]);

  const handleReset = useCallback(() => {
    chartRef.current?.getEchartsInstance?.()?.dispatchAction({ type: 'restore' });
  }, []);

  const baseOption = useMemo(() => ({
    ...option,
    tooltip: { ...buildTooltip(), ...(option?.tooltip ?? {}) },
    toolbox: { show: false },
    title: option?.title ?? { text: title, subtext: subtitle },
    backgroundColor: 'transparent',
    animationDuration: 900,
    animationEasing: 'cubicOut',
  }), [option, title, subtitle]);

  const needsUSAMap = useMemo(
    () => baseOption?.series?.some((series) => series.type === 'map' && series.map === 'USA')
      || baseOption?.geo?.map === 'USA',
    [baseOption]
  );

  const [isMapReady, setIsMapReady] = useState(() => {
    if (!needsUSAMap) return true;
    return Boolean(echarts.getMap && echarts.getMap('USA'));
  });
  const [mapFailed, setMapFailed] = useState(false);

  useEffect(() => {
    if (!needsUSAMap) {
      setIsMapReady(true);
      return undefined;
    }

    let cancelled = false;
    setIsMapReady(Boolean(echarts.getMap && echarts.getMap('USA')));

    ensureUSAMapRegistered().then((success) => {
      if (cancelled) return;
      setMapFailed(!success);
      setIsMapReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [needsUSAMap]);

  if (error) {
    return (
      <div className={`hc-surface rounded-4 p-4 d-flex flex-column justify-content-center align-items-center text-center shadow-sm ${className}`} style={{ minHeight: height, ...style }}>
        <div className="fw-bold hc-text-primary mb-2">Chart load error</div>
        <div className="text-secondary">{error.message || 'Unable to render chart.'}</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`hc-surface rounded-4 p-4 shadow-sm ${className}`} style={{ minHeight: height, ...style }}>
        <div className="skeleton position-relative overflow-hidden rounded-2 mb-3" style={{ height: 24, width: '40%' }} />
        <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 260 }} />
      </div>
    );
  }

  if (noData) {
    return (
      <div className={`hc-surface rounded-4 p-4 d-flex flex-column justify-content-center align-items-center text-center shadow-sm ${className}`} style={{ minHeight: height, ...style }}>
        <div className="fw-bold hc-text-primary mb-2">No data available</div>
        <div className="text-secondary">This chart is waiting for analytics data.</div>
      </div>
    );
  }

  if (needsUSAMap && mapFailed) {
    return (
      <div className={`hc-surface rounded-4 p-4 d-flex flex-column justify-content-center align-items-center text-center shadow-sm ${className}`} style={{ minHeight: height, ...style }}>
        <div className="fw-bold hc-text-primary mb-2">Map unavailable</div>
        <div className="text-secondary">Couldn&apos;t load the US map data. Check your network connection or the Assets/usa.json file.</div>
      </div>
    );
  }

  return (
    <>
      <div
        className={`hc-surface rounded-4 p-3 shadow-sm ${className}`}
        style={{
          position: 'relative',
          minHeight: height,
          ...(info ? { zIndex: infoOpen ? 30 : undefined } : {}),
          ...style,
        }}
      >
        <div className="basechart-toolbar">
          <button type="button" onClick={handleExportPNG} title="Export PNG" aria-label="Export chart as PNG">
            <MdFileDownload size={18} />
          </button>
          <button type="button" onClick={handleReset} title="Reset" aria-label="Reset chart">
            <MdRefresh size={18} />
          </button>
          <button type="button" onClick={handleOpenZoom} title="Zoom chart" aria-label="Open chart in fullscreen">
            <MdOpenInFull size={18} />
          </button>
          {info ? (
            <InfoHint info={info} className="hc-infohint--chart" onOpenChange={setInfoOpen} trigger="hover" />
          ) : null}
          {toolbarActions}
        </div>

        {isMapReady ? (
          <ReactECharts ref={chartRef} option={baseOption} className="w-100" style={{ height }} notMerge={true} lazyUpdate={true} />
        ) : (
          <div className="d-flex flex-column justify-content-center align-items-center text-center" style={{ height }}>
            <div className="skeleton position-relative overflow-hidden rounded-2 mb-3" style={{ height: 24, width: '40%' }} />
            <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 260 }} />
          </div>
        )}
      </div>

      <ChartZoomModal
        open={zoomOpen}
        onClose={handleCloseZoom}
        option={baseOption}
        title={title}
        subtitle={subtitle}
      />
    </>
  );
});

export default BaseChart;